import type { components } from "@/api/schema";

export type Call = components["schemas"]["Call"];
export type CallStatus = Call["status"];
export type CallMode = components["schemas"]["ModeEnum"];

export const SOFTPHONE: CallMode = 1;
export const BRIDGE: CallMode = 2;

/**
 * What the agent is told is happening.
 *
 * Deliberately coarser than `CallStatus`: `no_answer`, `busy`, `failed` and
 * `canceled` are four different facts for the call log and one fact for the
 * person holding the handset — it did not connect. The *reason* is shown
 * alongside, from `error_code`.
 */
export type CallPhase =
  "idle" | "starting" | "ringing" | "in_progress" | "ended" | "failed";

/** What the Twilio SDK knows, which is only ever about the browser's own leg. */
export type DeviceCallState = "idle" | "connecting" | "ringing" | "open";

const SERVER_PHASES: Record<CallStatus, CallPhase> = {
  pending: "starting",
  initiated: "starting",
  ringing: "ringing",
  in_progress: "in_progress",
  completed: "ended",
  no_answer: "failed",
  busy: "failed",
  failed: "failed",
  canceled: "failed",
};

const DEVICE_PHASES: Record<DeviceCallState, CallPhase> = {
  idle: "idle",
  connecting: "starting",
  ringing: "ringing",
  open: "in_progress",
};

/**
 * Narrow a status off the wire.
 *
 * A socket frame is untrusted input even though our own server sent it: it may
 * come from a deploy newer than the tab, carrying a status this bundle has
 * never heard of. Asserting would put an unmapped key into `SERVER_PHASES` and
 * render `undefined` into the call bar.
 */
export function parseCallStatus(raw: string): CallStatus | null {
  return raw in SERVER_PHASES ? (raw as CallStatus) : null;
}

export function phaseFromServer(status: CallStatus | null): CallPhase {
  return status === null ? "idle" : SERVER_PHASES[status];
}

export function phaseFromDevice(state: DeviceCallState): CallPhase {
  return DEVICE_PHASES[state];
}

export const TERMINAL_PHASES: readonly CallPhase[] = ["ended", "failed"];

export function isTerminal(phase: CallPhase): boolean {
  return TERMINAL_PHASES.includes(phase);
}

/**
 * Reconcile the two sources of truth. **The server wins.**
 *
 * The SDK sees the browser leg; the server sees the client leg, and the client
 * leg is the call. `client_leg_twiml` dials with `answerOnBridge`, so the SDK's
 * `ringing` and `accept` do track the client's phone rather than firing the
 * instant Twilio picks up — the two mostly agree, and this is not arbitration
 * between a liar and an honest witness.
 *
 * The server still wins, for the things only it knows: which of `no_answer`,
 * `busy`, `failed` or `canceled` a non-answer was, the duration on record, and
 * Twilio's error code. It is also the only source at all in bridge mode.
 *
 * What the SDK is better at is the first second, while `pending`/`initiated`
 * still mean "Twilio has not called us back yet". Only there does the device
 * get to speak.
 */
export function resolvePhase(
  server: CallPhase,
  device: CallPhase,
  mode: CallMode,
): CallPhase {
  // Bridge mode has no browser leg at all: the agent's mobile rings, and the
  // SDK is not involved (.docs/02 §3.2). Anything the device says here is
  // stale from a previous call.
  if (mode === BRIDGE) return server;
  if (server === "idle") return device;
  if (server !== "starting") return server;
  return device === "idle" ? server : device;
}

/**
 * Statuses only ever move forward.
 *
 * The socket and a refetch race, and out-of-order delivery is normal rather
 * than exceptional (`services.apply_status` guards the same thing server-side).
 * Without this a late `ringing` reopens a call the agent has already hung up.
 */
const RANK: Record<CallPhase, number> = {
  idle: 0,
  starting: 1,
  ringing: 2,
  in_progress: 3,
  ended: 4,
  failed: 4,
};

export function furthest(a: CallPhase, b: CallPhase): CallPhase {
  return RANK[b] > RANK[a] ? b : a;
}

/** `mm:ss`, and `h:mm:ss` once a call runs past the hour. */
export function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const parts = [Math.floor(safe / 60) % 60, safe % 60];
  const hours = Math.floor(safe / 3600);
  const tail = parts.map((part) => String(part).padStart(2, "0")).join(":");
  return hours > 0 ? `${hours}:${tail}` : tail;
}
